import { useState } from "react";
import { QuantityTiersField } from "@codway-dev/ui";
import type { QuantityTierItem } from "@codway-dev/ui";
import type {
  QuantityTiersEditorState,
  QuantityTierEditorTier,
} from "../../../store/quantity-tiers-editor.mapping";
import { useShopCurrency } from "../../../hooks/useShopCurrency";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function FieldPlaceholder({ height = 36 }: { height?: number }) {
  return (
    <div
      className="w-full rounded-md bg-gray-200/60"
      style={{ height: `${height}px` }}
    />
  );
}

function tierToPreviewItem(
  tier: QuantityTierEditorTier,
  basePrice: number,
  compareAtBase: number,
  formatPrice: (n: number) => string,
  fallbackImageUrl: string | undefined,
  hideProductImage: boolean,
  globalActiveBorderColor?: string,
  globalBackgroundColor?: string
): QuantityTierItem {
  let unitPrice = basePrice;
  if (tier.pricingAction === "fixed_price_per_item") {
    unitPrice = tier.pricingAmount;
  } else if (tier.pricingAction === "percentage_off") {
    unitPrice = basePrice * (1 - tier.pricingPercentage / 100);
  } else if (tier.pricingAction === "fixed_amount_off") {
    unitPrice = Math.max(0, basePrice - tier.pricingAmount);
  }

  const priceLabel = formatPrice(unitPrice * tier.quantity);
  const compareAtLabel =
    !tier.hideCompareAtPrice && unitPrice < compareAtBase
      ? formatPrice(compareAtBase * tier.quantity)
      : undefined;

  const badge = tier.badgeText
    ? {
        text: tier.badgeText,
        textColor: tier.badgeTextColor,
        backgroundColor: tier.badgeBackgroundColor,
      }
    : undefined;

  return {
    id: tier._tempId,
    quantity: tier.quantity,
    title: tier.title,
    priceLabel,
    compareAtLabel,
    badge,
    imageUrl: hideProductImage
      ? undefined
      : tier.customImageUrl || fallbackImageUrl,
    priceColor: tier.priceColor,
    titleColor: tier.titleColor,
    styles:
      globalActiveBorderColor || globalBackgroundColor
        ? {
            activeBorderColor: globalActiveBorderColor,
            borderColor: globalActiveBorderColor,
          }
        : undefined,
    isDefault: tier.isDefault,
  };
}

// ─── Preview ─────────────────────────────────────────────────────────────────

export function QuantityTiersEditorPreview({
  state,
}: {
  state: QuantityTiersEditorState;
}) {
  const { formatPrice } = useShopCurrency();
  const basePrice = parseFloat(state.targetProduct?.price ?? "0");
  const compareAtBase = state.useCompareAtPriceAsBase
    ? parseFloat(state.targetProduct?.compareAtPrice ?? "") || basePrice
    : basePrice;
  const fallbackImageUrl = state.targetProduct?.imageUrl;

  const defaultIndex = state.tiers.findIndex((t) => t.isDefault);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    defaultIndex >= 0 ? defaultIndex : null
  );
  // React's "adjust state on prop change" pattern — avoids an effect+setState
  // cascade. When the merchant toggles `isDefault` on a different tier,
  // snap the preview's selection to the new default (or clear it).
  const [prevDefaultIndex, setPrevDefaultIndex] = useState(defaultIndex);
  if (defaultIndex !== prevDefaultIndex) {
    setPrevDefaultIndex(defaultIndex);
    setSelectedIndex(defaultIndex >= 0 ? defaultIndex : null);
  }

  const variantOptions =
    state.targetProduct?.variants?.map((v) => ({ id: v.id, title: v.title })) ??
    [];

  const tiers = state.tiers.map((t) =>
    tierToPreviewItem(
      t,
      basePrice,
      compareAtBase,
      formatPrice,
      fallbackImageUrl,
      state.hideProductImage,
      state.activeBorderColor,
      state.backgroundColor
    )
  );

  return (
    <div className="sticky top-4">
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-icon type="view" />
            <s-text type="strong">Vista previa:</s-text>
          </s-stack>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-center text-sm font-bold tracking-wide text-gray-800 mb-4">
              PAGO CONTRA REEMBOLSO
            </div>

            <div className="flex flex-col gap-3">
              <FieldPlaceholder height={48} />

              {tiers.length > 0 && (
                <QuantityTiersField
                  tiers={tiers}
                  selectedIndex={selectedIndex}
                  onSelect={(i) => {
                    if (i !== null) setSelectedIndex(i);
                  }}
                  layout={state.layout}
                  backgroundColor={state.backgroundColor}
                  selectedBackgroundColor={state.selectedBackgroundColor}
                  activeBorderColor={state.activeBorderColor}
                  borderRadius={state.borderRadius}
                  variants={variantOptions}
                  hideVariantNumbers={state.hideVariantQuantitySelectors}
                  disableVariants={state.disableVariantSelectors}
                />
              )}

              <FieldPlaceholder height={48} />
              <FieldPlaceholder height={48} />
              <FieldPlaceholder height={40} />
            </div>
          </div>
        </s-stack>
      </s-box>
    </div>
  );
}
